import { useState } from 'react';
import DarkModeToggle from './components/DarkModeToggle';
import PlaylistTable from './components/PlaylistTable';
import type { PlaylistItem, ItemResult } from './components/PlaylistTable';
import { useDarkMode } from './hooks/useDarkMode';
import { useIsMobile } from './hooks/useIsMobile';
import { getColors } from './theme';
import { runPool, safeFilename, saveFile, sleep } from './playlistQueue';

type Format = 'mp3' | 'mp4';
type Scope = 'video' | 'playlist';

const CONCURRENCY = 3;

function isYouTubeUrl(u: string) {
  return /^(https?:\/\/)?((www|m|music)\.)?(youtube\.com\/(watch|playlist|shorts\/|live\/)|youtu\.be\/)/.test(u);
}

function hasPlaylist(u: string) {
  return /[?&]list=([a-zA-Z0-9_-]+)/.test(u);
}

function hasVideo(u: string) {
  return /(?:[?&]v=|youtu\.be\/|\/shorts\/|\/live\/|\/embed\/)([a-zA-Z0-9_-]{11})/.test(u);
}

export default function Converter() {
  const [dark, setDark] = useDarkMode();
  const mobile = useIsMobile();
  const [url, setUrl] = useState('');
  const [format, setFormat] = useState<Format>('mp3');
  const [scope, setScope] = useState<Scope>('playlist');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [items, setItems] = useState<PlaylistItem[]>([]);
  const [playlistTitle, setPlaylistTitle] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Record<string, ItemResult>>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const c = getColors(dark);
  const inputBg = dark ? '#111' : '#fff';
  const errorColor = dark ? '#f87171' : '#c00';

  const trimmed = url.trim();
  const bothAvailable = hasPlaylist(trimmed) && hasVideo(trimmed);
  const playlistMode = hasPlaylist(trimmed) && (scope === 'playlist' || !hasVideo(trimmed));

  function resetOutput() {
    setDownloadUrl(null);
    setError(null);
    setNotice(null);
    setItems([]);
    setSelected(new Set());
    setResults({});
    setTruncated(false);
  }

  async function convertOne(videoId: string): Promise<string | null> {
    setResults(prev => ({ ...prev, [videoId]: { status: 'converting' } }));
    try {
      const res = await fetch(`/api/convert?url=${videoId}&type=${format}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResults(prev => ({ ...prev, [videoId]: { status: 'ready', url: data.downloadUrl } }));
      return data.downloadUrl as string;
    } catch (err: any) {
      setResults(prev => ({
        ...prev,
        [videoId]: { status: 'error', error: err.message || 'Conversion failed' },
      }));
      return null;
    }
  }

  async function handleGo() {
    setError(null);
    setNotice(null);
    setDownloadUrl(null);
    if (!trimmed || !isYouTubeUrl(trimmed)) {
      setError('Paste a valid YouTube link.');
      return;
    }
    setLoading(true);
    try {
      if (playlistMode) {
        setItems([]);
        setResults({});
        const res = await fetch(`/api/playlist?url=${encodeURIComponent(trimmed)}`);
        const data = await res.json();
        if (data.error) {
          setError(data.error);
        } else {
          setItems(data.items);
          setPlaylistTitle(data.title);
          setTruncated(!!data.truncated);
          // Listing is free; converting is not. Select everything but wait for a click.
          setSelected(new Set(data.items.map((i: PlaylistItem) => i.videoId)));
        }
      } else {
        const res = await fetch(`/api/convert?url=${encodeURIComponent(trimmed)}&type=${format}`);
        const data = await res.json();
        if (data.error) setError(data.error);
        else setDownloadUrl(data.downloadUrl);
      }
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleBatch() {
    const targets = items.filter(i => selected.has(i.videoId));
    if (!targets.length) return;

    setBusy(true);
    setNotice(null);
    setError(null);

    const ready: Array<{ item: PlaylistItem; url: string }> = [];
    await runPool(targets, CONCURRENCY, async item => {
      const existing = results[item.videoId];
      const link = existing?.status === 'ready' && existing.url
        ? existing.url
        : await convertOne(item.videoId);
      if (link) ready.push({ item, url: link });
    });

    let opened = 0;
    for (const { item, url: link } of ready) {
      const how = await saveFile(link, safeFilename(item.title, format));
      if (how === 'opened') opened++;
      await sleep(600);
    }

    const failed = targets.length - ready.length;
    if (!ready.length) {
      setError('Nothing converted. Every selected item failed.');
    } else if (opened) {
      setNotice(
        `${ready.length} ready. Your browser blocked direct saving, so these opened in new tabs — ` +
        `allow pop-ups for this site, or use the per-row links.` +
        (failed ? ` ${failed} failed.` : '')
      );
    } else {
      setNotice(`Downloaded ${ready.length} file${ready.length === 1 ? '' : 's'}.` + (failed ? ` ${failed} failed.` : ''));
    }
    setBusy(false);
  }

  function handleUrlChange(val: string) {
    setUrl(val);
    resetOutput();
  }

  function handleFormatChange(f: Format) {
    setFormat(f);
    // Links are format-specific, so previous results no longer apply.
    setDownloadUrl(null);
    setError(null);
    setNotice(null);
    setResults({});
  }

  function toggle(videoId: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(videoId)) next.delete(videoId);
      else next.add(videoId);
      return next;
    });
  }

  function toggleAll() {
    setSelected(prev =>
      prev.size === items.length ? new Set() : new Set(items.map(i => i.videoId))
    );
  }

  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <div style={{
      minHeight: 'var(--vh100)', width: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: items.length ? 'flex-start' : 'center',
      // top padding clears the fixed Back link and theme toggle
      padding: `${mobile ? 76 : 96}px calc(16px + env(safe-area-inset-right, 0px)) calc(60px + env(safe-area-inset-bottom, 0px)) calc(16px + env(safe-area-inset-left, 0px))`,
      fontFamily: "'0xProto', 'JetBrains Mono', monospace",
      background: c.bg, color: c.fg,
      transition: 'background .35s ease, color .35s ease',
      boxSizing: 'border-box',
    }}>
      <a
        href="/"
        style={{
          position: 'fixed',
          top: 'calc(20px + env(safe-area-inset-top, 0px))',
          left: 'calc(20px + env(safe-area-inset-left, 0px))',
          display: 'flex', alignItems: 'center', gap: 6,
          textDecoration: 'none', color: c.fg,
          fontSize: 13, fontFamily: 'inherit',
          border: `1px solid ${c.hair}`,
          borderRadius: 8, padding: '8px 14px',
          transition: 'border-color .35s ease, color .35s ease',
          zIndex: 10,
        }}
      >
        ← Back
      </a>

      <DarkModeToggle dark={dark} onToggle={() => setDark(d => !d)} borderColor={c.hair} color={c.fg} />

      <h1 style={{
        fontFamily: "'0xProto', 'JetBrains Mono', monospace", fontWeight: 500,
        fontSize: mobile ? 22 : 'clamp(28px, 6vw, 40px)', letterSpacing: '0.01em',
        margin: mobile ? '0 0 22px' : '0 0 48px', textAlign: 'center',
      }}>
        YouTube Converter
      </h1>

      <div style={{
        display: 'flex', flexDirection: mobile ? 'column' : 'row',
        gap: 10, width: '100%', maxWidth: 720,
      }}>
        <input
          type="text"
          value={url}
          onChange={e => handleUrlChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleGo()}
          placeholder="Paste a YouTube video or playlist link..."
          style={{
            flex: 1, height: mobile ? 44 : 48, padding: '0 14px',
            // 16px minimum, or iOS Safari zooms the whole page on focus
            fontSize: 16, minWidth: 0,
            border: `1px solid ${c.hair}`, borderRadius: 8, outline: 'none',
            fontFamily: 'inherit', color: c.fg, background: inputBg,
            transition: 'background .35s ease, border-color .35s ease, color .35s ease',
            boxSizing: 'border-box',
          }}
        />
        <button
          onClick={handleGo}
          disabled={loading || busy}
          style={{
            height: mobile ? 44 : 48, padding: '0 24px', background: c.fg, color: c.bg,
            border: 'none', borderRadius: 8, fontSize: mobile ? 15 : 16, fontWeight: 500,
            cursor: loading || busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            opacity: loading || busy ? 0.6 : 1, whiteSpace: 'nowrap',
            transition: 'background .35s ease, color .35s ease',
          }}
        >
          {loading ? '...' : playlistMode ? 'List' : 'Go'}
        </button>
      </div>

      {bothAvailable && (
        <div style={{ display: 'flex', gap: 20, marginTop: 14, fontSize: 13, color: c.muted }}>
          {(['video', 'playlist'] as Scope[]).map(s => (
            <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                name="scope"
                checked={scope === s}
                onChange={() => { setScope(s); resetOutput(); }}
                style={{ accentColor: c.fg, width: 14, height: 14, cursor: 'pointer' }}
              />
              {s === 'video' ? 'This video' : 'Whole playlist'}
            </label>
          ))}
        </div>
      )}

      {error && (
        <p style={{ marginTop: 12, fontSize: mobile ? 12 : 13, color: errorColor, textAlign: 'center', maxWidth: 640 }}>
          {error}
        </p>
      )}

      {notice && (
        <p style={{ marginTop: 12, fontSize: mobile ? 12 : 13, color: c.muted, textAlign: 'center', maxWidth: 640 }}>
          {notice}
        </p>
      )}

      <div style={{ display: 'flex', gap: mobile ? 24 : 32, marginTop: mobile ? 18 : 28 }}>
        {(['mp3', 'mp4'] as Format[]).map(f => (
          <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: mobile ? 14 : 15, fontWeight: format === f ? 500 : 400, padding: '10px 6px' }}>
            <input
              type="radio"
              name="format"
              checked={format === f}
              onChange={() => handleFormatChange(f)}
              style={{ accentColor: c.fg, width: 16, height: 16, cursor: 'pointer' }}
            />
            {f.toUpperCase()}
          </label>
        ))}
      </div>

      {items.length > 0 && (
        <div style={{ width: '100%', maxWidth: 720, marginTop: 16 }}>
          <div style={{ fontSize: mobile ? 12 : 14, color: c.muted, textAlign: 'center' }}>
            {playlistTitle} · {items.length} videos
            {truncated && ' (first 400 shown)'}
          </div>

          <PlaylistTable
            items={items}
            selected={selected}
            results={results}
            c={c}
            mobile={mobile}
            format={format}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onRetry={convertOne}
          />

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <button
              onClick={handleBatch}
              disabled={busy || selected.size === 0}
              style={{
                height: mobile ? 44 : 48, padding: '0 24px', background: c.fg, color: c.bg,
                border: 'none', borderRadius: 8, fontSize: mobile ? 14 : 15, fontWeight: 500,
                cursor: busy || selected.size === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: busy || selected.size === 0 ? 0.5 : 1,
              }}
            >
              {busy
                ? 'Working...'
                : allSelected
                  ? `Download all (${selected.size})`
                  : `Download selected (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      {downloadUrl && (
        <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%', maxWidth: 560 }}>
          <div style={{ width: '100%', height: 1, background: c.hair }} />
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '13px 32px', border: `1px solid ${c.fg}`, borderRadius: 8,
              textDecoration: 'none', color: c.fg, fontSize: 15, fontWeight: 500,
              fontFamily: 'inherit',
            }}
          >
            ↓ Download {format.toUpperCase()}
          </a>
        </div>
      )}
    </div>
  );
}
